/* eslint-disable react/jsx-max-depth */
import { memo } from 'react';

import { ExternalLink } from '@doger/ui/components/ui/link';

import { LogAmplitudeEventOnMount } from '@doger/chat/shared/analytics';

import styles from './privacy-policy.module.scss';

const lastUpdated = '18.09.2025';

export const PrivacyPolicyPage = memo(() => (
  <>
    <LogAmplitudeEventOnMount event='Privacy Policy Page Viewed' />

    <div className={styles.privacyPolicyPage}>
      <div className={styles.container}>
        <h1 className={styles.title}>PRIVACY POLICY</h1>

        <div className={styles.content}>
          <div className={styles.versionInfo}>
            <p className={styles.paragraph}>
              V.1.0. Last Updated {lastUpdated}
            </p>
          </div>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>1. GENERAL INFORMATION</h2>
            <ol className={styles.simpleList}>
              <li>
                <span className={styles.counter}>1.1.</span> The proper handling
                of Personal Data is extremely important to Us (Kuna Tech DMCC).
                We therefore take your privacy seriously when processing and
                protecting your Personal Data. This is done in accordance with
                provisions set out in the applicable privacy laws (including the
                General Data Protection Regulation). This Privacy Policy applies
                to an Application / Services of DOGER provided by Us. If you do
                not agree with provisions of this Privacy Policy, you have to
                restrain from using the Application/Services. By registering for
                services in the Application/Website, and checking the box "I
                agree with provisions of Terms and Privacy Policy" (or by opt
                for usage of any of our Services provided) you provide Us with
                an informed consent to process and use your Personal Data as
                specified in this Privacy Policy. Please be advised that certain
                limits for actual services may be imposed until some PD is
                provided.
              </li>
              <li>
                <span className={styles.counter}>1.2.</span> We take great care
                to limit the Personal Data We collect and use only for the
                purposes, which are necessary for providing our Services or
                which are connected with such provision.
              </li>
              <li>
                <span className={styles.counter}>1.3.</span>{' '}
                <strong>Legal grounds for processing.</strong>
                <ol className={styles.simpleList}>
                  <li>
                    <span className={styles.counter}>1.3.1.</span>{' '}
                    <strong>
                      Existence of a contract between the User and Company:
                    </strong>{' '}
                    By registering in Application or opting for Services via
                    other available means you and Company enter into a contract
                    for the provision of Services, as is stipulated in the
                    Terms. In order to provide you with Services as a whole, as
                    well as with a separate part of it, We have to collect and
                    process certain Personal Data about You;
                  </li>
                  <li>
                    <span className={styles.counter}>1.3.2.</span>{' '}
                    <strong>Legitimate interest:</strong> Company uses a
                    legitimate interest in processing of your Personal Data, for
                    example, when Company processes your data to fix bugs in the
                    Application / Services and to ensure its smooth operation,
                    to improve the work of the our Services, to develop and to
                    introduce new functions and services, to ensure security of
                    User when using the Application, and to ensure secure work
                    of the Services, to transfer data within the organizational
                    structure of Company, etc. We will not use legitimate
                    interest as a basis for processing of the User data if the
                    rights, freedoms and interests of the user override the
                    interest of Company or third-party;
                  </li>
                  <li>
                    <span className={styles.counter}>1.3.3.</span>{' '}
                    <strong>Legislative requirements:</strong> Company may
                    process user's Personal Data, if it is required by the law
                    that applies to Kuna Tech DMCC.
                  </li>
                  <li>
                    <span className={styles.counter}>1.3.4.</span>{' '}
                    <strong>Consent:</strong> Company relies solely on Your
                    consent when uses Personal Data for the purpose of
                    registering in Application, usage of Application / Services
                    offered, etc.
                  </li>
                  <li>
                    <span className={styles.counter}>1.3.5.</span> We do not
                    transfer your Personal Data to third parties, except
                    circumstances mentioned in this Privacy Policy or Terms.
                  </li>
                </ol>
              </li>
              <li>
                <span className={styles.counter}>1.4.</span> This Privacy Policy
                is subject to the Terms of Use (which may be found in the
                Application/Website). Your use of the Application and any
                Personal Data you provide remains subject to the terms of this
                Privacy Policy and our Terms of Use.
              </li>
              <li>
                <span className={styles.counter}>1.5.</span> In the event of
                significant updating of this Privacy Policy, We will ask you to
                re-consent to the updated Privacy Policy before any changes take
                effect and continuous use of the Services/Application shall be
                deemed Your consent. In the event that significant changes are
                unacceptable to you, We ask you to stop using Our App/Services
                immediately. All changes will indicate the date from which they
                will take effect.
              </li>
              <li>
                <span className={styles.counter}>1.6.</span> The definitions set
                forth herein and mentioned with capital letters, unless
                specifically introduced in this Privacy Policy, shall have the
                meaning as described in Terms of Use or, if the definition
                cannot be found in Terms of Use, shall have the meaning as is
                specified in the General Data Protection Regulation.
              </li>
            </ol>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>2. ABOUT US</h2>
            <ol className={styles.simpleList}>
              <li>
                <span className={styles.counter}>2.1.</span> The legal entity,
                which acts as a Personal Data Processing Company is{' '}
                <strong>Kuna Tech DMCC</strong> (incorporated under the laws of
                Dubai, United Arab Emirates, Company number: DMCC192968, Licence
                Number: DMCC-841617, registered address: Unit No: 1706, Jumeirah
                Bay Tower X3, Plot No: JLT-PH2-X3A, Jumeirah Lakes Towers,
                Dubai, the United Arab Emirates).
              </li>
            </ol>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              3. WHAT DATA WE COLLECT ABOUT YOU
            </h2>
            <ol className={styles.simpleList}>
              <li>
                <span className={styles.counter}>3.1.</span> «Personal Data»
                (also referred to as the «PD», «Personal Information») shall
                mean any information that identifies you, i.e. your email, login
                (username) requests to our support team, and etc. If you cannot
                be identified (for example, when Personal Data has been
                anonymized or this information solely bears statistical
                information), this Policy shall not apply.
              </li>
              <li>
                <span className={styles.counter}>3.2.</span> Whenever you use
                our Application, We collect and process your Personal Data.
              </li>
              <li>
                <span className={styles.counter}>3.3.</span> We divide the
                information we collect about you as follows:
                <ol className={styles.simpleList}>
                  <li>
                    <span className={styles.counter}>3.3.1.</span>{' '}
                    <strong>
                      Information you provide us with by yourself:
                    </strong>
                    <ol className={styles.simpleList}>
                      <li>
                        <span className={styles.counter}>3.3.1.1.</span> When
                        you register (or opt for the Services in another way)
                        and then use our Services, you provide Us with your
                        Personal Data which includes following info:
                        <ol className={styles.simpleList}>
                          <li>
                            <span className={styles.counter}>3.3.1.1.1.</span>{' '}
                            Name and Surname;
                          </li>
                          <li>
                            <span className={styles.counter}>3.3.1.1.2.</span>{' '}
                            Email;
                          </li>
                          <li>
                            <span className={styles.counter}>3.3.1.1.3.</span>{' '}
                            Telegram contact information (optional field and can
                            be skipped);
                          </li>
                        </ol>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <span className={styles.counter}>3.3.2.</span>{' '}
                    <strong>
                      Information We collect automatically (may apply even if
                      You do not register or use offered Services):
                    </strong>
                    <ol className={styles.simpleList}>
                      <li>
                        <span className={styles.counter}>3.3.2.1.</span> We
                        collect general anonymous analytical information on the
                        use of the Services for their optimization.
                      </li>
                    </ol>
                  </li>
                  <li>
                    <span className={styles.counter}>3.3.3.</span>{' '}
                    <strong>Information We receive from third parties.</strong>{' '}
                    We do not exclude the possibility of collecting your
                    Personal Data from other sources, for example, from publicly
                    available sources. We use this information in addition to
                    the Personal Data We have already collected about you in
                    order to better personalize and improve our services for
                    you, as well as to verify the Personal Data you provide Us
                    with.
                  </li>
                </ol>
              </li>
            </ol>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              4. HOW WE USE YOUR PERSONAL DATA
            </h2>
            <ol className={styles.simpleList}>
              <li>
                <span className={styles.counter}>4.1.</span> We may deliver,
                personalize, and improve our Application/Services by combining
                and using the information We have about you (including
                information We receive on and off our Application) to understand
                how you use and interact with our Application, Services and the
                people or things you're connected to and interested in. We also
                may use the information We have about you for the following
                purposes:
                <ol className={styles.simpleList}>
                  <li>
                    <span className={styles.counter}>4.1.1.</span> Provide,
                    maintain, improve, and develop relevant features, content,
                    and Services, Application;
                  </li>
                  <li>
                    <span className={styles.counter}>4.1.2.</span> Fulfil your
                    requests (support) and when authorized by you;
                  </li>
                  <li>
                    <span className={styles.counter}>4.1.3.</span> Research and
                    develop new Services;
                  </li>
                  <li>
                    <span className={styles.counter}>4.1.4.</span> Detect and
                    defend against fraudulent, abusive, or unlawful activity;
                  </li>
                  <li>
                    <span className={styles.counter}>4.1.5.</span> Send You
                    technical notices, updates, security alerts, invoices and
                    other support and administrative messages;
                  </li>
                  <li>
                    <span className={styles.counter}>4.1.6.</span> Provide
                    customer (User) service;
                  </li>
                  <li>
                    <span className={styles.counter}>4.1.7.</span> Communicate
                    with you about products, Services, offers;
                  </li>
                  <li>
                    <span className={styles.counter}>4.1.8.</span> To send you
                    different promotions, and info regarding events, and to
                    provide other news and information we think will be of
                    interest to you;
                  </li>
                  <li>
                    <span className={styles.counter}>4.1.9.</span> Respond to
                    requests from individuals to access, correct, or delete
                    their information, or requests to exercise other rights
                    related to their information under applicable law
                  </li>
                  <li>
                    <span className={styles.counter}>4.1.10.</span> Create
                    aggregated statistical data, inferred non-personal data, or
                    anonymized or pseudonymized data (rendered non-personal and
                    non-identifiable), which we or our business partners or
                    customers may use to provide and improve our respective
                    services
                  </li>
                  <li>
                    <span className={styles.counter}>4.1.11.</span> Comply with
                    our contractual and legal obligations, resolve disputes with
                    Users, and enforce our agreements
                  </li>
                  <li>
                    <span className={styles.counter}>4.1.12.</span> Carry out
                    any other purpose described at the time the information was
                    collected
                  </li>
                  <li>
                    <span className={styles.counter}>4.1.13.</span> Using for
                    notification if any changes are applied to
                    Services/Application.
                  </li>
                </ol>
              </li>
            </ol>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              5. HOW WE SHARE YOUR PERSONAL DATA
            </h2>
            <ol className={styles.simpleList}>
              <li>
                <span className={styles.counter}>5.1.</span> Our Privacy Policy
                applies only to the Services and Application. They sometimes may
                contain links to other websites, sources. We do not exercise
                control over any website that may be linked to from within
                content on our Services/App. We are not responsible for the
                privacy practices or content of such websites.
              </li>
              <li>
                <span className={styles.counter}>5.2.</span>{' '}
                <strong>
                  We do not transfer your Personal Data to third parties, except
                  cases, that are mentioned in this Privacy Policy which are
                  next:
                </strong>
                <ol className={styles.simpleList}>
                  <li>
                    <span className={styles.counter}>5.2.1.</span>{' '}
                    <strong>With our partners.</strong> We may share information
                    with affiliates, vendors, consultants, and other service
                    providers (but not with advertisers and ad partners) who
                    need access to such information to carry out work for Us.
                    The partner's use of Personal Data will be subject to
                    appropriate confidentiality and security measures.
                  </li>
                  <li>
                    <span className={styles.counter}>5.2.2.</span>{' '}
                    <strong>To comply with the law.</strong> We may share
                    information in response to a request for information if We
                    believe disclosure is in accordance with, or required by,
                    any applicable law, regulation, legal process or
                    governmental request, including, but not limited to, meeting
                    national security or law enforcement requirements. To the
                    extent the law allows it, We will attempt to provide you
                    with prior notice before disclosing your information in
                    response to such a request.
                  </li>
                  <li>
                    <span className={styles.counter}>5.2.3.</span>{' '}
                    <strong>In an emergency.</strong> We may share information
                    if We believe it's necessary to prevent imminent and serious
                    bodily harm to a person;
                  </li>
                  <li>
                    <span className={styles.counter}>5.2.4.</span>{' '}
                    <strong>To enforce our policies and rights.</strong> We may
                    share information if We believe your actions are
                    inconsistent with our user agreements, rules, or other
                    Orovera policies, or to protect the rights, property, and
                    safety of ourselves and others;
                  </li>
                  <li>
                    <span className={styles.counter}>5.2.5.</span>{' '}
                    <strong>With our Affiliates.</strong> We may share
                    information between and among Company and any of our
                    parents, affiliates, subsidiaries, and other companies under
                    common control and ownership;
                  </li>
                  <li>
                    <span className={styles.counter}>5.2.6.</span>{' '}
                    <strong>With your consent.</strong> We may share information
                    about you with your consent or at your discretion.
                  </li>
                </ol>
              </li>
              <li>
                <span className={styles.counter}>5.3.</span> Other information
                that does not personally identify you as an individual is
                collected by Company (such as, by way of example, patterns of
                use) and is exclusively owned by Us. We can use this information
                in such manner on our sole discretion, deems appropriate.
              </li>
              <li>
                <span className={styles.counter}>5.4.</span> We may share
                specific aggregated, non-Personal Data with third parties, such
                as the number of users who have registered with us, the volume
                and pattern of traffic to and within the App, Services etc. That
                information will not identify you, the individual, in any way.
              </li>
              <li>
                <span className={styles.counter}>5.5.</span> As was mentioned
                above, when you send Us messages, We can keep them for
                administering of your inquiries, for improving of our services.
                We shall not transfer information from such messages to third
                parties.
              </li>
              <li>
                <span className={styles.counter}>5.6.</span> In other cases, We
                do not share your Personal Data, but retain it encrypted on our
                servers, for more details about that please see section
                "Security of Your Personal Data".
              </li>
            </ol>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>6. LENGTH OF DATA RETENTION</h2>
            <ol className={styles.simpleList}>
              <li>
                <span className={styles.counter}>6.1.</span> We retain your
                Personal Data to fulfill our legal, operational, contractual and
                regulatory obligations. For each type of records, the
                recordkeeping requirements may vary so We guarantee that in no
                event shall We retain your Personal Data longer than required.
                If you would like to know more about the length of retention for
                a specific data, please contact Us.
              </li>
            </ol>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              7. SECURITY OF YOUR PERSONAL DATA
            </h2>
            <ol className={styles.simpleList}>
              <li>
                <span className={styles.counter}>7.1.</span> We provide the
                following safety measures regarding protection of your Personal
                Data:
                <ol className={styles.simpleList}>
                  <li>
                    <span className={styles.counter}>7.1.1.</span> Evaluation of
                    the effectiveness of used security measures, prior to the
                    launch of the Application/Services and theirs updates;
                  </li>
                  <li>
                    <span className={styles.counter}>7.1.2.</span> Establishing
                    rules to access to Personal Data processed by Company, as
                    well as ensuring registration and recording of all actions
                    performed with PD in the information systems of the
                    Application/Services;
                  </li>
                  <li>
                    <span className={styles.counter}>7.1.3.</span> Detection of
                    the facts of unauthorized access to PD and the adoption of
                    appropriate response measures;
                  </li>
                  <li>
                    <span className={styles.counter}>7.1.4.</span> Restoration
                    of PD that was modified or destroyed due to unauthorized
                    access to them (within the framework of technical capability
                    of the Company).
                  </li>
                </ol>
              </li>
              <li>
                <span className={styles.counter}>7.2.</span>{' '}
                <strong>
                  WE USE, STORE AND PROCESS YOUR PD ON SERVERS IN VARIOUS
                  JURISDICTIONS (EU-COUNTRIES, SOMETIMES OTHERS), WHERE OUR
                  FACILITIES AND/OR SERVICE PROVIDERS ARE LOCATED. BY USING
                  SERVICES YOU AGREE TO THIS TRANSFER, STORING, OR PROCESSING.
                  COMPANY WILL TAKE ALL STEPS REASONABLY NECESSARY TO ENSURE
                  THAT YOUR PERSONAL DATA IS TREATED SECURELY AND IN ACCORDANCE
                  WITH THIS POLICY
                </strong>
                . Some of the safeguards We use are firewalls and data
                encryption, physical access controls to our data centers, and
                information access authorization controls. Company also
                authorize access to Personal Data only for those employees or
                contractors who require it to fulfil their job or service
                responsibilities. All of our physical, electronic, and
                procedural safeguards are designed to comply with applicable
                laws and regulations. From time to time, the Personal Data may
                be also stored in other locations, and in such cases, Company
                will ensure that the Personal Data will be stored and processed
                with the reasonable level of care and security.
              </li>
            </ol>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>8. KNOW YOUR RIGHTS</h2>
            <ol className={styles.simpleList}>
              <li>
                <span className={styles.counter}>8.1.</span> If you are a
                resident of the European Union, you have certain data protection
                rights under the GDPR:
                <ol className={styles.simpleList}>
                  <li>
                    <span className={styles.counter}>8.1.1.</span>{' '}
                    <strong>You can access and change</strong> certain types of
                    the Personal Data through the Application without contacting
                    Company (this clause also applies for the non-EU residents);
                  </li>
                  <li>
                    <span className={styles.counter}>8.1.2.</span>{' '}
                    <strong>
                      You may delete several types of your account information,
                      which were provided by yourself, at any time from the
                      relevant page
                    </strong>{' '}
                    (if it is available at the moment due to the beta-character
                    of the Services provided)<strong>.</strong> But We may
                    retain certain your Personal Data as required by law or for
                    legitimate business purposes after you delete your account;
                  </li>
                  <li>
                    <span className={styles.counter}>8.1.3.</span>{' '}
                    <strong>
                      You have the right to receive information regarding
                      purposes of Personal Data
                    </strong>{' '}
                    collection, regarding third parties, to which we disclose
                    your Personal Data, information, regarding safeguards that
                    are applicable for secure storage of your Personal Data,
                    period of time for which Personal Data will be stored;
                  </li>
                  <li>
                    <span className={styles.counter}>8.1.4.</span>{' '}
                    <strong>
                      You also have the right to demand full erasure of Personal
                      Data concerning you sending Us a request via support.
                    </strong>{' '}
                    We shall fulfil your request in timely manner. If there is
                    no other lawful ground for storing and processing of your
                    PD, as prescribed by Applicable Law, Company shall erase
                    such information. If there is another legal ground for such
                    processing, Company shall notify you in appropriate period
                    of time (this clause also applies for the non-EU residents);
                  </li>
                  <li>
                    <span className={styles.counter}>8.1.5.</span> You also have
                    the right to demand restriction of the storing and
                    processing of the PD, if legal grounds for such demands have
                    arisen / or are prescribed in Applicable Law;
                  </li>
                  <li>
                    <span className={styles.counter}>8.1.6.</span> You have the
                    right to object processing of Personal Data concerning
                    yourself under circumstances, that are prescribed in
                    Applicable Law. In case of such objection, Company shall
                    restrain from processing of your PD, if there will be no
                    other legal ground for continuing of processing;
                  </li>
                  <li>
                    <span className={styles.counter}>8.1.7.</span>{' '}
                    <strong>
                      You have the right to ask the Company to provide you with
                      a structured list of previously provided PD
                    </strong>{' '}
                    (and of the currently processed PD by the Company). You also
                    can ask Us to transmit your PD to previously chosen third
                    parties.
                  </li>
                </ol>
              </li>
              <li>
                <span className={styles.counter}>8.2.</span> To send a request
                for delete, rectification and submission of PD please contact
                support.
              </li>
            </ol>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>9. DATA TRANSFERS</h2>
            <ol className={styles.simpleList}>
              <li>
                <span className={styles.counter}>9.1.</span> When we transfer PD
                outside the European Union or EFTA, we ensure that the adequate
                level of PD security is provided. During such transfer, we also
                ensure that adequate level of your rights protection is also
                applied, on the basis of adequacy analysis of the third country
                Personal Data protection legislation, on the basis of
                contractual obligations, assumed by the PD recipient.
              </li>
            </ol>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>10. NOTIFICATION</h2>
            <ol className={styles.simpleList}>
              <li>
                <span className={styles.counter}>10.1.</span> If the Company
                becomes aware of a security systems breach, then we will attempt
                to notify you electronically so that you can take appropriate
                protective steps.The Company may post a notice on our websites
                or in Application if the security breach occurs.
              </li>
              <li>
                <span className={styles.counter}>10.2.</span> When the Personal
                Data breach is likely to result in a high risk to the rights and
                freedoms of Users, the Company will inform you as soon as
                reasonably possible.
              </li>
              <li>
                <span className={styles.counter}>10.3.</span> In the event that
                Company is acquired by or merged with a third-party entity, we
                reserve the right, in any of these circumstances, to transfer or
                assign the information we have collected from our Users as part
                of such merger, acquisition, sale, or other change of control.
                If we become involved in a merger, acquisition, or any form of
                sale of some or all of its assets, We will notify Users before
                Personal Data is transferred and becomes subject to a different
                privacy policy. In the unlikely event of our bankruptcy,
                insolvency, reorganization, receivership, or assignment for the
                benefit of creditors, or the application of laws or equitable
                principles affecting creditors' rights generally, we may not be
                able to control how your Personal Data is treated, transferred,
                or used.
              </li>
            </ol>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              11. COLLECTION OF CHILDREN'S PD
            </h2>
            <ol className={styles.simpleList}>
              <li>
                <span className={styles.counter}>11.1.</span>{' '}
                <strong>
                  PERSONNEL OF COMPANY DOES NOT KNOWINGLY COLLECT OR MAINTAIN
                  PERSONALLY IDENTIFIABLE INFORMATION OR NON-PERSONALLY
                  IDENTIFIABLE INFORMATION ON OUR SERVICES FROM PERSONS UNDER 14
                  YEARS OLD, AND NO PART OF OUR SERVICES IS DIRECTED TO PERSONS
                  UNDER 14. IF YOU ARE UNDER 14 YEARS OLD, THEN PLEASE DO NOT
                  USE OR ACCESS THE COMPANY SERVICES AT ANY TIME OR IN ANY
                  MANNER. IF COMPANY LEARNS THAT PERSONALLY IDENTIFIABLE
                  INFORMATION OF PERSONS LESS THAN 14 YEARS OLD HAS BEEN
                  COLLECTED ON THE SERVICES WITHOUT VERIFIED PARENTAL CONSENT,
                  THEN COMPANY WILL TAKE THE APPROPRIATE STEPS TO DELETE THIS
                  INFORMATION AND SUSPEND YOUR ACCOUNT.
                </strong>
              </li>
            </ol>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>12. CONTACT US</h2>
            <p className={styles.paragraph}>
              If You have any questions about this Privacy Policy, please feel
              free to contact Us or to write to Us at:
              <br /> E-mail:{' '}
              <ExternalLink href='mailto:doger@layer2state.com'>
                doger@layer2state.com
              </ExternalLink>
            </p>
          </section>
        </div>
      </div>
    </div>
  </>
));
